import { __decorate } from "tslib";
import { Options as Component, Watch, Prop, Vue, Inject } from 'vue-property-decorator';
import { window } from '../../core/browser.mjs';
import { mdiFastForward10, mdiPlayCircleOutline, mdiPauseCircleOutline, mdiReplay, mdiRewind10 } from '@mdi/js';
// TODO: Remove property from package.json. Now only in v1_material
import clamp from 'clamp-js';
const EVENT_BUS = 'eventBus';
import MdSpinner from '../mdSpinner/mdSpinner.vue';
var PlayerStatus;
(function (PlayerStatus) {
    PlayerStatus["Loading"] = "loading";
    PlayerStatus["Playing"] = "playing";
    PlayerStatus["Stopped"] = "stopped";
    PlayerStatus["Finished"] = "finished";
    PlayerStatus["Error"] = "error";
})(PlayerStatus || (PlayerStatus = {}));
let AudioId = 1000;
let AudioPlayer = class AudioPlayer extends Vue {
    mdiFastForward10 = mdiFastForward10;
    mdiPlayCircleOutline = mdiPlayCircleOutline;
    mdiPauseCircleOutline = mdiPauseCircleOutline;
    mdiReplay = mdiReplay;
    mdiRewind10 = mdiRewind10;
    name;
    url;
    file;
    simple;
    preload;
    disabled;
    progress;
    disabledCaption;
    minCompletionTime;
    audioInstance;
    beforePlay;
    providerUrl;
    eventBus;
    player = undefined;
    status = PlayerStatus.Stopped;
    loaded = false;
    audioId = undefined;
    statusList = PlayerStatus;
    position = 0;
    disabledByError = false;
    disabledByErrorCaption = '';
    listened = false;
    listenedTimer = 0;
    firstPause = true;
    onUrlChanged() {
        this.initPlayer();
    }
    onFileChanged() {
        this.initPlayer();
    }
    get backdropStyle() {
        return {
            width: this.position + '%',
        };
    }
    get returnProviderUrl() {
        return `${window.location.origin}${this.providerUrl}`;
    }
    get duration() {
        return this.player && this.player.duration;
    }
    mounted() {
        this.audioId = AudioId++;
        this.formatName();
        if (this.url || this.file) {
            this.initPlayer();
        }
    }
    formatName() {
        const element = this.$el.querySelector('.player-title > span');
        if (element) {
            clamp(element, { clamp: 3 });
        }
    }
    encodeURL(url) {
        // manages  special characters (+&#) on Filenames (Last Uri component)
        return url
            .split('/')
            .map((c, i) => (i > 0 ? encodeURIComponent(c) : c))
            .join('/');
    }
    getCurrentTime() {
        return this.player && this.player.currentTime;
    }
    loadAudio(showSpinner = true) {
        return new Promise((res, rej) => {
            if (this.loaded) {
                res(true);
            }
            else {
                if (showSpinner) {
                    this.status = PlayerStatus.Loading;
                }
                if (this.player) {
                    this.player.load();
                    this.player.addEventListener('canplay', () => {
                        this.loaded = true;
                        this.$emit('audio-loaded');
                        res(true);
                    }, { once: true });
                }
            }
        });
    }
    sendPlayedEvent(event) {
        this.firstPause = false;
        const currentPoint = (event && event.path && event.path.length && event.path[0].currentTime) || 0;
        const miliseconds = Math.round(currentPoint * 1000);
        this.$emit('played', { ...event, duration: miliseconds });
    }
    initPlayer() {
        if (this.audioInstance) {
            this.player = this.audioInstance;
        }
        else if (this.url) {
            this.player = new Audio();
            this.player.preload = this.preload;
            this.player.src = this.encodeURL(this.url);
        }
        else if (this.file) {
            this.player = new Audio();
            let reader = new FileReader();
            reader.onload = (e) => {
                this.player.src = e.target.result;
            };
            reader.readAsDataURL(this.file);
        }
        this.position = 0;
        this.status = PlayerStatus.Stopped;
        this.player.addEventListener('error', (err) => {
            this.disabledByError = true;
            this.status = PlayerStatus.Error;
            this.$emit('audio-error', this.player.src);
        });
        this.player.addEventListener('loadedmetadata', () => {
            this.$emit('loadedmetadata', this.player);
        });
        this.player.addEventListener('play', (event) => {
            this.$emit('play', event);
        });
        this.player.addEventListener('pause', (event) => {
            if (this.firstPause) {
                this.sendPlayedEvent(event);
            }
            this.$emit('pause', event);
        });
        this.player.addEventListener('seeked', (event) => {
            this.$emit('seeked', event);
        });
        this.player.addEventListener('ended', (event) => {
            if (this.firstPause) {
                this.sendPlayedEvent(event);
            }
            this.$emit('ended', event);
            this.finish();
        });
        this.player.addEventListener('timeupdate', (event) => {
            if (this.progress) {
                this.updatePosition();
            }
            this.$emit('timeupdate', event);
        });
        this.eventBus.on('play', (id) => {
            if (this.status === PlayerStatus.Playing && this.audioId && this.audioId !== id) {
                this.pause();
            }
        });
    }
    async playAudio() {
        if (this.status !== PlayerStatus.Playing) {
            await this.loadAudio();
            this.player.play();
            this.status = PlayerStatus.Playing;
            this.eventBus.emit('play', this.audioId);
            this.eventBus.emit('trackEvent', 'Samples', 'Play sample', 'Sample - ' + this.name);
        }
    }
    play() {
        const self = this;
        this.status = PlayerStatus.Loading;
        this.$nextTick();
        if (typeof this.beforePlay === 'function') {
            this.beforePlay()
                .then(() => {
                self.playAudio();
            })
                .catch((e) => {
                this.$emit('before-play-fail', e);
            });
        }
        else {
            this.playAudio();
        }
    }
    stop() {
        if (this.status === PlayerStatus.Playing) {
            this.status = PlayerStatus.Stopped;
            this.player.pause();
            this.emitAudioListened();
        }
    }
    pause() {
        this.status = PlayerStatus.Stopped;
        this.player.pause();
        this.$emit('audio-paused');
        this.emitAudioListened();
    }
    finish() {
        this.status = PlayerStatus.Finished;
        this.emitAudioListened();
    }
    reset() {
        this.player.currentTime = 0;
        this.status = PlayerStatus.Stopped;
    }
    skip(percentage) {
        const duration = this.player.duration;
        let delta = (duration * percentage) / 100;
        delta = delta > 0 ? Math.max(delta, 10) : Math.min(delta, -10);
        const position = this.player.currentTime + delta;
        const newPosition = Math.max(0, Math.min(position, duration));
        this.player.currentTime = newPosition;
        return newPosition;
    }
    seek(percentage) {
        this.player.currentTime = this.player.duration * percentage;
    }
    playButton(e) {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        if (this.status === this.statusList.Finished) {
            this.play();
        }
        else if (this.status === this.statusList.Playing) {
            this.pause();
        }
        else {
            this.play();
        }
    }
    updatePosition() {
        const duration = this.player.duration;
        if (this.status === PlayerStatus.Playing) {
            this.position = (this.player.currentTime * 100) / duration;
            if (!this.listenedTimer) {
                this.listenedTimer = setTimeout(this.emitAudioListened, this.minCompletionTime * 1000);
            }
        }
    }
    titleClick() {
        this.$emit('title-click');
    }
    emitAudioListened() {
        if (!this.listened) {
            this.$emit('audio-listened');
            this.listened = true;
        }
    }
    hasSlots() {
        return this.$slots['left'] !== undefined || this.$slots['right'] !== undefined;
    }
    hasBottomSlots() {
        return this.$slots['bottom-right'] !== undefined;
    }
    hasPlayerTitleSlot() {
        return this.$slots['player-title'] !== undefined;
    }
    hasHeadOptions() {
        return this.$slots['headOptions'] !== undefined;
    }
    unmounted() {
        if (this.player) {
            this.player.pause();
            this.player.remove();
        }
    }
};
__decorate([
    Prop()
], AudioPlayer.prototype, "name", void 0);
__decorate([
    Prop()
], AudioPlayer.prototype, "url", void 0);
__decorate([
    Prop()
], AudioPlayer.prototype, "file", void 0);
__decorate([
    Prop({ default: false })
], AudioPlayer.prototype, "simple", void 0);
__decorate([
    Prop({ default: 'none' })
], AudioPlayer.prototype, "preload", void 0);
__decorate([
    Prop({ default: false })
], AudioPlayer.prototype, "disabled", void 0);
__decorate([
    Prop({ default: true })
], AudioPlayer.prototype, "progress", void 0);
__decorate([
    Prop({ default: '' })
], AudioPlayer.prototype, "disabledCaption", void 0);
__decorate([
    Prop({ default: 2 })
], AudioPlayer.prototype, "minCompletionTime", void 0);
__decorate([
    Prop()
], AudioPlayer.prototype, "audioInstance", void 0);
__decorate([
    Prop()
], AudioPlayer.prototype, "beforePlay", void 0);
__decorate([
    Prop({ default: '' })
], AudioPlayer.prototype, "providerUrl", void 0);
__decorate([
    Inject({ from: EVENT_BUS })
], AudioPlayer.prototype, "eventBus", void 0);
__decorate([
    Watch('url')
], AudioPlayer.prototype, "onUrlChanged", null);
__decorate([
    Watch('file')
], AudioPlayer.prototype, "onFileChanged", null);
AudioPlayer = __decorate([
    Component({
        components: {
            MdSpinner,
        },
    })
], AudioPlayer);
export default AudioPlayer;
//# sourceMappingURL=AudioPlayer.mjs.map